/**
 * Shared modal behavior (UX-AUDIT-2026-07 X-3): Escape closes, focus moves
 * into the dialog on open and restores on close, Tab cycles inside, and the
 * backdrop closes only when BOTH mousedown and mouseup land on the backdrop
 * itself — a drag-select ending outside a form must never destroy it.
 *
 * When opts.isDirty() is provided and returns true, close requests flip
 * `confirming` instead of closing; the modal renders its own inline confirm
 * (never window.confirm) and calls the close callback explicitly to discard.
 *
 * ONLY THE TOPMOST MODAL LISTENS. Every instance binds its key handler to
 * `document`, so without this a stack of two answered one Escape twice. On the
 * account board that is not hypothetical: a PO detail opens ON TOP of the
 * transactions drill, and one Escape while reading the PO closed the PO, the
 * drill underneath it, and returned the reader to the board. Tab is guarded
 * the same way, or the outer trap fights the inner one for focus.
 *
 * Topmost is decided by DOCUMENT ORDER, not by who registered first. Effects
 * run child-before-parent, so two modals opened in the same commit register
 * inner-first and registration order names the OUTER one as topmost — the
 * wrong one, and the first thing the test for this caught. Document order is
 * right for both shapes that occur: a nested modal is contained by its parent
 * (and containment counts as following), and one portalled to `<body>` is
 * appended after the modal it opened from.
 *
 * Usage:
 *   const modal = useModalBehavior(onClose, { isDirty: () => form !== initial });
 *   <div className="xxx-overlay" {...modal.overlayProps}>
 *     <div className="xxx-modal" {...modal.dialogProps}> … </div>
 *   </div>
 */
import { useState, useRef, useEffect } from 'react';

/**
 * Every enabled modal. Module scope on purpose: the handlers are on
 * `document`, so the arbitration has to be global too.
 */
const modalStack = [];

/** DOCUMENT_POSITION_FOLLOWING — set for a later sibling AND for a descendant. */
const FOLLOWING = 4;

/**
 * True when no other enabled modal sits above `token` — i.e. none of their
 * containers follows this one in the document.
 *
 * A modal can be enabled while rendering `null` (a loading gate above its own
 * overlay), and it has no container to compare. Returning false there would
 * silently kill its Escape, so that case falls back to registration order —
 * which is what this arbitration used before document order replaced it.
 */
function isTopmost(token) {
  const mine = token.el();
  if (!mine) return modalStack[modalStack.length - 1] === token;
  return !modalStack.some((other) => {
    if (other === token) return false;
    const el = other.el();
    if (!el || el === mine) return false;
    return !!(mine.compareDocumentPosition(el) & FOLLOWING);
  });
}

export default function useModalBehavior(onClose, opts = {}) {
  const { isDirty, enabled = true } = opts;
  const [confirming, setConfirming] = useState(false);
  const containerRef = useRef(null);
  const prevFocus = useRef(null);
  const downTarget = useRef(null);
  const stateRef = useRef({});
  stateRef.current = { isDirty, onClose };

  const requestClose = () => {
    const s = stateRef.current;
    if (s.isDirty && s.isDirty()) {
      setConfirming(true);
      return;
    }
    s.onClose();
  };

  useEffect(() => {
    if (!enabled) return undefined;
    setConfirming(false);
    const token = { el: () => containerRef.current };
    modalStack.push(token);
    prevFocus.current = document.activeElement;
    const t = setTimeout(() => {
      const el = containerRef.current && containerRef.current.querySelector(
        'input, select, textarea, button, [tabindex]:not([tabindex="-1"])');
      if (el) el.focus();
    }, 0);
    const onKey = (e) => {
      // A modal with another one stacked above it is not the one being
      // dismissed, and must not steal Tab from it either.
      if (!isTopmost(token)) return;
      if (e.key === 'Escape') {
        e.stopPropagation();
        requestClose();
      } else if (e.key === 'Tab' && containerRef.current) {
        const focusables = containerRef.current.querySelectorAll(
          'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])');
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', onKey);
      const at = modalStack.indexOf(token);
      if (at >= 0) modalStack.splice(at, 1);
      if (prevFocus.current && prevFocus.current.focus) prevFocus.current.focus();
    };
  }, [enabled]); // deliberately not exhaustive: handlers read stateRef

  const overlayProps = {
    onMouseDown: (e) => { downTarget.current = e.target; },
    onClick: (e) => {
      if (e.target === e.currentTarget && downTarget.current === e.currentTarget) requestClose();
    },
  };
  const dialogProps = { ref: containerRef, role: 'dialog', 'aria-modal': 'true' };
  return { overlayProps, dialogProps, confirming, setConfirming, requestClose };
}
